import { ChangeEventHandler, useCallback, useState, useEffect } from 'react';

import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import SearchIcon from '@mui/icons-material/Search';
import { Box, InputAdornment, TextField } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { scroller } from 'react-scroll';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';

import { Typography } from 'components/atoms/Typography';
import { useGetPageConfig } from 'hooks/api/builder/useGetPageConfig';
import { useAppDispatch, useAppSelector } from 'hooks/store/useAppStore';
import { setSelectedPage } from 'store/slices/pageBuilderSlice';
import { PageList } from 'types/api/pageList';

import { MenuTreeWrapper, NodeTreeScrollContainer } from './MenuTree.styled';
import { ModalMenuTreeWrapper } from './ModalMenuTree.styled';
import { PageSelectTree } from './PageSelectTree';

interface MenuTreeProps {
    pages: PageList[];
    loading: boolean;
    error: boolean;
    onModalPageSelect: (show: boolean) => void;
}

export const MenuTree = ({
    pages,
    loading,
    error,
    onModalPageSelect,
}: MenuTreeProps): JSX.Element => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { selectedPage } = useAppSelector((state) => state.pageBuilder);
    const { page } = useGetPageConfig(selectedPage || 0, false);

    const { pathname } = useLocation();

    useEffect(() => {
        const currentPageByPath = pages.find((el) => el.href === pathname);
        // set current page when page reloading
        if (currentPageByPath) {
            dispatch(setSelectedPage(currentPageByPath.id));
        }
    }, [dispatch, pages, pathname]);

    const phone = useBreakpoint(down('lg'));

    const onPageSelect = useCallback(
        (id: number, href: string): void => {
            if (id !== selectedPage) {
                if (phone) {
                    onModalPageSelect(false);
                }

                navigate(href, { replace: true });
                dispatch(setSelectedPage(Number(id)));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, navigate, onModalPageSelect, selectedPage]
    );

    useEffect(() => {
        scroller.scrollTo(`scroll_el_${selectedPage || page?.id}`, {
            duration: 350,
            delay: 0,
            smooth: true,
            containerId: 'menu_elements',
            offset: -150,
        });
    }, [selectedPage, page?.id]);

    // if parent page config has empty config
    useEffect(() => {
        if (page?.pageConfig?.length === 0) {
            const childPage = pages.filter(
                (el) => el.route_uuid === page.uuid
            )[0];

            if (childPage) {
                navigate(childPage.href);
                dispatch(setSelectedPage(childPage.id));
            }
        }
    }, [dispatch, navigate, page, pages]);

    const [searchValue, setSearchValue] = useState('');

    const onSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
        const { value } = e.target;
        const temp = value.replace(
            // eslint-disable-next-line no-useless-escape
            /[\.,-\/#!$%\^&\*;:{}=\-_`~()"'+@<>?\\\[\]]/g,
            ''
        );

        setSearchValue(temp);
    };

    const searchFieldStyles = phone
        ? {
              '& .MuiInputBase-root': {
                  fontSize: '14px',
                  padding: '6px 4px',
              },
              '& svg': {
                  width: '1.1em',
                  height: '1.1em',
              },
          }
        : {
              '& .MuiInputBase-root': {
                  fontSize: '18px',
                  padding: '4px 0px',
              },
              '& svg': {
                  width: '1.3em',
                  height: '1.3em',
              },
          };

    const setMenuTree = (): JSX.Element => {
        return (
            <MenuTreeWrapper>
                <NodeTreeScrollContainer
                    className="menu_elements"
                    id="menu_elements"
                >
                    <Box
                        sx={{
                            px: phone ? '12px' : '16px',
                            pb: '20px',
                        }}
                    >
                        <TextField
                            size="medium"
                            variant="standard"
                            placeholder="Поиск"
                            value={searchValue}
                            onChange={onSearch}
                            fullWidth
                            sx={searchFieldStyles}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position="start"
                                        sx={{ marginRight: '5px' }}
                                    >
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment
                                        position="end"
                                        sx={{
                                            cursor: 'pointer',
                                            visibility: searchValue
                                                ? 'visible'
                                                : 'hidden',
                                        }}
                                        onClick={() => setSearchValue('')}
                                    >
                                        <ClearRoundedIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    {!loading && !error ? (
                        <PageSelectTree
                            pages={pages}
                            selectedPage={selectedPage}
                            searchValue={searchValue}
                            onPageSelect={onPageSelect}
                        />
                    ) : undefined}

                    {!loading && !error && !pages.length ? (
                        <Box sx={{ px: '16px', pb: '16px' }}>
                            <Typography>
                                На данный момент страницы отсутствуют
                            </Typography>
                        </Box>
                    ) : undefined}
                    {!loading && error && !pages.length ? (
                        <Box sx={{ px: '16px', pb: '16px' }}>
                            <Typography>
                                При загрузке страниц что-то пошло не так...
                            </Typography>
                        </Box>
                    ) : undefined}
                </NodeTreeScrollContainer>
            </MenuTreeWrapper>
        );
    };

    if (phone) {
        return <ModalMenuTreeWrapper> {setMenuTree()} </ModalMenuTreeWrapper>;
    }

    return setMenuTree();
};
